import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Sparkles, Users, Wallet, Music, Calendar, ChevronRight,
  Loader2, Zap, Camera, RotateCcw, Share2, Instagram, Copy
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ICONS = { vibe: Sparkles, guests: Users, budget: Wallet, music: Music, occasion: Calendar };

const OPTIONS = {
  vibe: [
    { label: "Chill & Intimate", value: "chill", desc: "Low-key, warm, cozy" },
    { label: "Wild & Energetic", value: "wild", desc: "Loud music, dance floor" },
    { label: "Elegant & Classy", value: "elegant", desc: "Sophisticated, premium" },
    { label: "Themed & Fun", value: "themed", desc: "Bollywood, retro, neon" },
    { label: "Minimal & Aesthetic", value: "minimal", desc: "Clean, Insta-worthy" },
  ],
  guests: [
    { label: "Just Us (2-5)", value: "2-5", desc: "Intimate gathering" },
    { label: "Small Group (6-15)", value: "6-15", desc: "Close friends & family" },
    { label: "Medium Party (16-30)", value: "16-30", desc: "Proper party!" },
    { label: "Big Bash (30+)", value: "30+", desc: "Go big or go home" },
  ],
  budget: [
    { label: "Under 4K", value: "under 4000", desc: "Budget-friendly" },
    { label: "4K - 6K", value: "4000-6000", desc: "Good balance" },
    { label: "6K - 10K", value: "6000-10000", desc: "Premium setup" },
    { label: "10K - 15K", value: "10000-15000", desc: "Luxury experience" },
    { label: "15K+", value: "above 15000", desc: "No limits!" },
  ],
  music: [
    { label: "Bollywood Hits", value: "bollywood", desc: "Desi beats all night" },
    { label: "EDM / Pop", value: "edm", desc: "High energy drops" },
    { label: "Romantic / Soft", value: "romantic", desc: "Love songs & ballads" },
    { label: "Retro / Old School", value: "retro", desc: "90s & 2000s vibes" },
    { label: "Mix Everything", value: "mix", desc: "All genres, all vibes" },
  ],
  occasion: [
    { label: "Birthday", value: "Birthday", desc: "Happy Birthday!" },
    { label: "Anniversary", value: "Anniversary", desc: "Celebrating love" },
    { label: "Baby Shower", value: "Baby Shower", desc: "New beginnings" },
    { label: "Housewarming", value: "Housewarming", desc: "Welcome home" },
    { label: "Just Because!", value: "Just Because", desc: "Who needs a reason?" },
  ],
};

const STEPS = ["vibe", "guests", "budget", "music", "occasion"];

const CreatorDNAQuiz = ({ onResult }) => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [quizVersion, setQuizVersion] = useState(null);
  const [photo, setPhoto] = useState(null);
  const photoRef = useRef(null);

  // Fetch random quiz version
  useEffect(() => {
    fetch(`${API_URL}/api/party-dna/quiz-version`)
      .then(r => r.json())
      .then(d => setQuizVersion(d.version))
      .catch(() => {});
  }, []);

  const handleSelect = (value) => {
    const key = STEPS[step];
    const newAnswers = { ...answers, [key]: value };
    setAnswers(newAnswers);
    if (step < STEPS.length - 1) {
      setTimeout(() => setStep(s => s + 1), 300);
    }
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setPhoto(reader.result.split(",")[1]);
    reader.readAsDataURL(file);
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await fetch(`${API_URL}/api/party-dna/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...answers, photo_b64: photo || "" }),
      });
      if (res.ok) {
        const data = await res.json();
        setResult(data);
        if (onResult) onResult(data.personality, answers);
      }
    } catch { /* silent */ }
    setGenerating(false);
  };

  const handleRetake = () => {
    setStep(0);
    setAnswers({});
    setResult(null);
    setPhoto(null);
    // Get a new version
    fetch(`${API_URL}/api/party-dna/quiz-version`)
      .then(r => r.json())
      .then(d => setQuizVersion(d.version))
      .catch(() => {});
  };

  const currentKey = STEPS[step];
  const currentOptions = OPTIONS[currentKey] || [];
  const Icon = ICONS[currentKey] || Sparkles;
  const questionText = quizVersion?.[currentKey] || `What's your ${currentKey}?`;
  const allAnswered = STEPS.every(k => answers[k]);

  // Result view
  if (result?.personality) {
    const p = result.personality;
    const shareText = `I just discovered my Party DNA: ${p.personality_name}! ${p.personality_description || ""} \n\nFind yours at DoozyGo Creator Studio! @doozygo #PartyDNA #DoozyGo #HouseParty`;
    
    return (
      <div className="space-y-4 animate-fadeIn" data-testid="creator-dna-result">
        <div className="text-center py-6 bg-gradient-to-br from-[#E83E8C]/20 to-[#7C3AED]/20 rounded-2xl border border-[#E83E8C]/20">
          <p className="text-xs text-white/40 uppercase tracking-wider mb-2">Your Party DNA</p>
          <h2 className="text-2xl font-bold bg-gradient-to-r from-[#E83E8C] to-[#FF9F1C] bg-clip-text text-transparent">{p.personality_name}</h2>
          <p className="text-sm text-white/60 mt-2 max-w-sm mx-auto px-4">{p.personality_description}</p>
          {p.style_keywords && (
            <div className="flex flex-wrap justify-center gap-2 mt-3">
              {p.style_keywords.map(kw => (
                <span key={kw} className="px-2.5 py-0.5 rounded-full bg-white/10 text-white/50 text-[10px]">{kw}</span>
              ))}
            </div>
          )}
        </div>

        {/* Share DNA Result on Social */}
        <div className="space-y-2">
          <p className="text-[10px] font-bold text-white/40 uppercase tracking-wider text-center">Share your Party DNA</p>
          <div className="flex items-center justify-center gap-2 flex-wrap">
            <button onClick={async () => {
              if (navigator.share) {
                try { await navigator.share({ text: shareText }); return; } catch { /* fallback */ }
              }
              window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, "_blank");
            }}
              className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#25D366] text-white text-[10px] font-bold" data-testid="dna-share-wa">
              <Share2 size={12} /> WhatsApp
            </button>
            <button onClick={() => {
              const text = encodeURIComponent(`I'm a ${p.personality_name}! What's your Party DNA? Find out at DoozyGo Creator Studio @doozygo`);
              window.open(`https://www.instagram.com/`, "_blank");
              navigator.clipboard?.writeText(`I'm a ${p.personality_name}! ${p.personality_description || ""}\n\nFind your Party DNA at DoozyGo Creator Studio! @doozygo\n#PartyDNA #DoozyGo #HouseParty #MumbaiParty`);
            }}
              className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-gradient-to-r from-[#833AB4] via-[#FD1D1D] to-[#F77737] text-white text-[10px] font-bold" data-testid="dna-share-ig">
              <Instagram size={12} /> Instagram (copied!)
            </button>
            <button onClick={() => { navigator.clipboard?.writeText(shareText); }}
              className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white/70 text-[10px] font-bold" data-testid="dna-copy-text">
              <Copy size={12} /> Copy Text
            </button>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex gap-3">
          <button onClick={handleRetake} className="flex-1 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white/60 text-xs font-medium flex items-center justify-center gap-1">
            <RotateCcw size={12} /> New Version
          </button>
          <button onClick={() => {
            onResult(p, answers);
            // Scroll up to the upload area
            setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 100);
          }} className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-[#E83E8C] to-[#FF9F1C] text-white text-xs font-bold flex items-center justify-center gap-1" data-testid="use-dna-result">
            <Sparkles size={12} /> Use This & Create Content
          </button>
        </div>
      </div>
    );
  }

  // Generate view (after all answered)
  if (allAnswered && step >= STEPS.length) {
    return (
      <div className="space-y-4" data-testid="creator-dna-generate">
        <div className="text-center py-4">
          <Zap size={32} className="text-[#E83E8C] mx-auto mb-2" />
          <h3 className="text-lg font-bold text-white">Ready to discover your Party DNA?</h3>
          <p className="text-xs text-white/40 mt-1">Add a photo (optional) to get a personalized share card</p>
        </div>
        {/* Optional photo */}
        <div className="text-center">
          {photo ? (
            <div className="inline-block relative">
              <img src={`data:image/jpeg;base64,${photo}`} alt="You" className="w-20 h-20 rounded-full object-cover border-2 border-[#E83E8C]" />
              <button onClick={() => setPhoto(null)} className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-white text-[10px] flex items-center justify-center">x</button>
            </div>
          ) : (
            <button onClick={() => photoRef.current?.click()} className="px-4 py-2 rounded-full bg-white/5 border border-white/10 text-white/50 text-xs">
              <Camera size={12} className="inline mr-1" /> Add Your Photo
            </button>
          )}
          <input ref={photoRef} type="file" className="hidden" accept="image/*" onChange={handlePhotoUpload} />
        </div>
        <button onClick={handleGenerate} disabled={generating}
          className="w-full py-4 rounded-2xl bg-gradient-to-r from-[#E83E8C] to-[#7C3AED] text-white font-bold text-base disabled:opacity-40 flex items-center justify-center gap-2" data-testid="creator-dna-generate-btn">
          {generating ? <><Loader2 size={18} className="animate-spin" /> Analyzing your DNA...</> : <><Zap size={18} /> Reveal My Party DNA</>}
        </button>
        <button onClick={() => setStep(0)} className="block mx-auto text-xs text-white/30 hover:text-white/50">Start over</button>
      </div>
    );
  }

  // Quiz steps
  return (
    <div className="space-y-4" data-testid="creator-dna-quiz">
      {/* Progress */}
      <div className="flex gap-1.5">
        {STEPS.map((s, i) => (
          <div key={s} className={`flex-1 h-1.5 rounded-full transition-all ${i <= step ? "bg-[#E83E8C]" : "bg-white/10"}`} />
        ))}
      </div>
      <p className="text-[10px] text-white/30 text-center">Question {step + 1} of {STEPS.length}</p>

      {/* Question */}
      <div className="text-center py-2">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#E83E8C] to-[#7C3AED] flex items-center justify-center mx-auto mb-3">
          <Icon size={22} className="text-white" />
        </div>
        <h3 className="text-lg font-bold text-white">{questionText}</h3>
      </div>

      {/* Options */}
      <div className="space-y-2">
        {currentOptions.map(opt => (
          <button key={opt.value} onClick={() => handleSelect(opt.value)}
            className={`w-full text-left p-4 rounded-xl border transition-all ${
              answers[currentKey] === opt.value
                ? "border-[#E83E8C] bg-[#E83E8C]/10"
                : "border-white/10 bg-white/5 hover:border-white/20 hover:bg-white/10"
            }`} data-testid={`creator-dna-${currentKey}-${opt.value}`}>
            <span className="text-sm font-semibold text-white">{opt.label}</span>
            <p className="text-[10px] text-white/40 mt-0.5">{opt.desc}</p>
          </button>
        ))}
      </div>

      {/* Navigation */}
      {step > 0 && (
        <button onClick={() => setStep(s => s - 1)} className="text-xs text-white/30 hover:text-white/50 block mx-auto">Back</button>
      )}

      {/* Auto-advance to generate after last question */}
      {allAnswered && step === STEPS.length - 1 && (
        <button onClick={() => setStep(STEPS.length)} className="w-full py-3 rounded-xl bg-gradient-to-r from-[#E83E8C] to-[#7C3AED] text-white font-bold text-sm flex items-center justify-center gap-2 mt-2" data-testid="creator-dna-next">
          <ChevronRight size={14} /> Next
        </button>
      )}
    </div>
  );
};

export default CreatorDNAQuiz;
